export type YoyTone = 'pos' | 'neg' | 'neutral';

const DASH = '–';

const isNum = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

/**
 * Display unit for all money values in the dashboard. API values are rupees.
 * To show crores instead, swap to: { key: 'Cr', label: '₹ Cr', divisor: 1e7 }
 */
export const MONEY_UNIT = { key: 'L', label: '₹ L', divisor: 1e5 };
// export const MONEY_UNIT = { key: 'Cr', label: '₹ Cr', divisor: 1e7 };

/** Unit label for headers/axes, e.g. "₹ L". */
export const moneyLabel = (): string => MONEY_UNIT.label;

/** Rupees -> display unit as a number rounded to 2 decimals (for charts). */
export const toMoneyUnit = (rupees: number): number => Math.round((rupees / MONEY_UNIT.divisor) * 100) / 100;

/** Format an already-converted display-unit value, en-IN grouping, 2 decimals. */
export function formatUnitValue(v: number | null | undefined): string {
  if (!isNum(v)) return DASH;
  const s = v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return s === '-0.00' ? '0.00' : s;
}

/** Rupees -> display unit (lakhs by default), 2 decimals, en-IN grouping. Unit lives in labels. */
export function formatMoney(rupees: number | null | undefined): string {
  if (!isNum(rupees)) return DASH;
  return formatUnitValue(rupees / MONEY_UNIT.divisor);
}

/** Rupees -> lakhs. */
export function formatLakh(rupees: number | null | undefined): string {
  if (!isNum(rupees)) return DASH;
  return formatUnitValue(rupees / 1e5);
}

/** Rupees -> Crores, 2 decimals, number only. The unit ("Cr") lives in labels. */
export function formatCr(rupees: number | null | undefined): string {
  if (!isNum(rupees)) return DASH;
  const v = rupees / 1e7;
  // avoid "-0.00"
  const s = v.toFixed(2);
  return s === '-0.00' ? '0.00' : s;
}

/** Signed percentage with 1 decimal. */
export function formatPct(n: number | null | undefined): string {
  if (!isNum(n)) return DASH;
  const s = n.toFixed(1);
  if (s === '0.0' || s === '-0.0') return '0.0%';
  return `${n > 0 ? '+' : ''}${s}%`;
}

export function yoyTone(n: number | null | undefined): YoyTone {
  if (!isNum(n)) return 'neutral';
  const s = n.toFixed(1);
  if (s === '0.0' || s === '-0.0') return 'neutral';
  return n > 0 ? 'pos' : 'neg';
}

export function formatInt(n: number | null | undefined): string {
  if (!isNum(n)) return DASH;
  return Math.round(n).toLocaleString('en-IN');
}
